import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import {
  AlertTriangle,
  ArrowLeft,
  Heart,
  Pause,
  Gift,
  MessageSquare,
  CheckCircle,
  XCircle,
  RefreshCw
} from "lucide-react";

const API_URL = process.env.REACT_APP_API_URL;

export default function CancelSubscription() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: Reason, 2: Offer, 3: Confirm
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [reasons, setReasons] = useState([]);
  const [subscription, setSubscription] = useState(null);
  
  const [selectedReason, setSelectedReason] = useState("");
  const [reasonDetails, setReasonDetails] = useState("");
  const [feedback, setFeedback] = useState("");
  const [willingToDiscuss, setWillingToDiscuss] = useState(false);
  const [immediate, setImmediate] = useState(false);
  
  const [retentionOffer, setRetentionOffer] = useState(null);
  const [canPause, setCanPause] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    const token = localStorage.getItem("speakr_token");
    
    try {
      const [reasonsRes, statusRes] = await Promise.all([
        fetch(`${API_URL}/api/subscription/cancellation/reasons`),
        fetch(`${API_URL}/api/subscription/status/${user?.business_id}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      if (reasonsRes.ok) {
        const data = await reasonsRes.json();
        setReasons(data.reasons || []);
      }
      if (statusRes.ok) {
        const data = await statusRes.json();
        setSubscription(data);
      }
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const initiateCancel = async () => {
    if (!selectedReason) {
      toast.error("Please select a reason");
      return;
    }
    
    setProcessing(true);
    const token = localStorage.getItem("speakr_token");
    
    try {
      const res = await fetch(`${API_URL}/api/subscription/cancellation/initiate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          customer_id: user?.business_id,
          reason_id: selectedReason,
          reason_details: reasonDetails,
          feedback,
          willing_to_discuss: willingToDiscuss
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        setRetentionOffer(data.retention_offer);
        setCanPause(data.can_pause);
        setStep(2);
      } else {
        toast.error("Failed to process request");
      }
    } catch (error) {
      toast.error("Failed to process request");
    } finally {
      setProcessing(false);
    }
  };

  const confirmCancel = async () => {
    setProcessing(true);
    const token = localStorage.getItem("speakr_token");
    
    try {
      const res = await fetch(`${API_URL}/api/subscription/cancellation/confirm`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          customer_id: user?.business_id,
          reason_id: selectedReason,
          reason_details: reasonDetails,
          feedback,
          immediate
        })
      });
      
      if (res.ok) {
        setStep(3);
      } else {
        toast.error("Failed to cancel subscription");
      }
    } catch (error) {
      toast.error("Failed to cancel subscription");
    } finally {
      setProcessing(false);
    }
  };

  const acceptOffer = async (offerType) => {
    setProcessing(true);
    const token = localStorage.getItem("speakr_token");
    
    try {
      const res = await fetch(`${API_URL}/api/subscription/cancellation/accept-offer?customer_id=${user?.business_id}&offer_type=${offerType}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        navigate("/dashboard/billing");
      } else {
        toast.error("Failed to apply offer");
      }
    } catch (error) {
      toast.error("Failed to apply offer");
    } finally {
      setProcessing(false);
    }
  };

  const pauseSubscription = async () => {
    setProcessing(true);
    const token = localStorage.getItem("speakr_token");
    
    try {
      const res = await fetch(`${API_URL}/api/subscription/pause`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          customer_id: user?.business_id,
          pause_months: 1,
          reason: reasonDetails
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        navigate("/dashboard/billing");
      } else {
        toast.error("Failed to pause subscription");
      }
    } catch (error) {
      toast.error("Failed to pause subscription");
    } finally {
      setProcessing(false);
    }
  };

  const getFollowUpQuestion = () => {
    const reason = reasons.find(r => r.id === selectedReason);
    return reason?.follow_up_question;
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  // Step 3: Confirmation
  if (step === 3) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-slate-50 to-white p-6">
        <Card className="max-w-md w-full">
          <CardContent className="pt-8 text-center space-y-4">
            <div className="w-16 h-16 mx-auto bg-emerald-100 rounded-full flex items-center justify-center">
              <CheckCircle className="w-8 h-8 text-emerald-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900">Subscription Cancelled</h1>
            <p className="text-slate-600">
              Your subscription has been cancelled.
              {!immediate && subscription?.current_period_ends && (
                <> You'll continue to have access until {new Date(subscription.current_period_ends).toLocaleDateString()}.</>
              )}
            </p>
            <p className="text-sm text-slate-500">
              We're sorry to see you go. If you change your mind, you can always resubscribe from your dashboard.
            </p>
            <Button onClick={() => navigate("/dashboard")} className="mt-4">
              Go to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Step 2: Retention Offer
  if (step === 2) {
    return (
      <div className="p-6 max-w-2xl mx-auto space-y-6">
        <Button variant="ghost" onClick={() => setStep(1)} className="mb-4">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        
        <div className="text-center mb-8">
          <div className="w-16 h-16 mx-auto bg-blue-100 rounded-full flex items-center justify-center mb-4">
            <Heart className="w-8 h-8 text-blue-600" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900">We'd hate to see you go!</h1>
          <p className="text-slate-500 mt-2">Before you leave, we have an offer for you</p>
        </div>

        {retentionOffer && (
          <Card className="border-2 border-blue-200 bg-gradient-to-b from-blue-50 to-white">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Gift className="w-5 h-5 text-blue-600" />
                {retentionOffer.title}
              </CardTitle>
              <CardDescription>{retentionOffer.description}</CardDescription>
            </CardHeader>
            <CardContent>
              <Button 
                className="w-full" 
                onClick={() => acceptOffer(retentionOffer.offer_type)}
                disabled={processing}
              >
                {retentionOffer.action_label}
              </Button>
            </CardContent>
          </Card>
        )}

        {canPause && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Pause className="w-5 h-5 text-amber-600" />
                Need a Break?
              </CardTitle>
              <CardDescription>
                Pause your subscription for up to 3 months instead of cancelling
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button 
                variant="outline" 
                className="w-full"
                onClick={pauseSubscription}
                disabled={processing}
              >
                Pause for 1 Month
              </Button>
            </CardContent>
          </Card>
        )}

        <Card className="border-red-200">
          <CardHeader>
            <CardTitle className="text-red-600 flex items-center gap-2">
              <XCircle className="w-5 h-5" />
              Continue with Cancellation
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="immediate">Cancel immediately</Label>
                <p className="text-sm text-slate-500">
                  Otherwise, you'll retain access until {subscription?.current_period_ends ? new Date(subscription.current_period_ends).toLocaleDateString() : "end of billing period"}
                </p>
              </div>
              <Switch
                id="immediate"
                checked={immediate}
                onCheckedChange={setImmediate}
              />
            </div>
            
            <Button 
              variant="destructive" 
              className="w-full"
              onClick={confirmCancel}
              disabled={processing}
            >
              {processing ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : null}
              Cancel My Subscription
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Step 1: Reason Selection
  return (
    <div className="p-6 max-w-2xl mx-auto space-y-6">
      <Button variant="ghost" onClick={() => navigate("/dashboard/billing")} className="mb-4">
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Billing
      </Button>
      
      <div className="text-center mb-8">
        <div className="w-16 h-16 mx-auto bg-amber-100 rounded-full flex items-center justify-center mb-4">
          <AlertTriangle className="w-8 h-8 text-amber-600" />
        </div>
        <h1 className="text-2xl font-bold text-slate-900">Cancel Subscription</h1>
        <p className="text-slate-500 mt-2">We're sorry to hear you're thinking of leaving. Help us understand why.</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Why are you cancelling?</CardTitle>
          <CardDescription>Select the main reason for your cancellation</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <RadioGroup value={selectedReason} onValueChange={setSelectedReason}>
            {reasons.map(reason => (
              <div key={reason.id} className="flex items-start space-x-3 p-3 rounded-lg hover:bg-slate-50">
                <RadioGroupItem value={reason.id} id={reason.id} className="mt-1" />
                <div>
                  <Label htmlFor={reason.id} className="cursor-pointer font-medium">
                    {reason.label}
                  </Label>
                </div>
              </div>
            ))}
          </RadioGroup>

          {selectedReason && getFollowUpQuestion() && (
            <div className="space-y-2 p-4 bg-slate-50 rounded-lg">
              <Label>{getFollowUpQuestion()}</Label>
              <Textarea
                value={reasonDetails}
                onChange={(e) => setReasonDetails(e.target.value)}
                placeholder="Your answer..."
                rows={3}
              />
            </div>
          )}

          <div className="space-y-2">
            <Label>Any additional feedback?</Label>
            <Textarea
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              placeholder="Help us improve by sharing your thoughts..."
              rows={3}
            />
          </div>

          <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
            <div className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-blue-600" />
              <div>
                <Label htmlFor="discuss">Would you be open to discussing this with us?</Label>
                <p className="text-sm text-slate-500">We might be able to help solve your concerns</p>
              </div>
            </div>
            <Switch
              id="discuss"
              checked={willingToDiscuss}
              onCheckedChange={setWillingToDiscuss}
            />
          </div>

          <Button 
            className="w-full" 
            onClick={initiateCancel}
            disabled={!selectedReason || processing}
          >
            {processing ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : null}
            Continue
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
